import { useNotification } from "@context/NotificationContext";
import {
  claimTaskByNominationId,
  completeTaskByNominationId,
  unclaimTaskByNominationId,
  type CompleteTaskByNominationIdArgs,
} from "@services/tasksApi";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

type MutationFeedback = {
  successTitle?: string;
  successDescription?: string;
  errorTitle: string;
};

type ClaimTaskVariables = MutationFeedback & {
  nominationId: string;
};

type CompleteTaskVariables = MutationFeedback & {
  nominationId: string;
  params: CompleteTaskByNominationIdArgs;
};

export const useTask = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess?: () => void;
  handleError?: () => void;
}) => {
  const { notify } = useNotification();

  const notifySuccess = ({
    successTitle,
    successDescription,
  }: Pick<MutationFeedback, "successTitle" | "successDescription">) => {
    if (!successTitle) return;

    notify({
      title: successTitle,
      description: successDescription,
      type: "success",
    });
  };

  const notifyError = (error: AxiosError<{ detail?: string }>, errorTitle: string) => {
    notify({
      title: errorTitle,
      description: error.response?.data?.detail ?? error.message,
      type: "error",
    });
  };

  const claimTaskMutation = useMutation({
    mutationFn: ({ nominationId }: ClaimTaskVariables) => claimTaskByNominationId(nominationId),
    onSuccess: (_, variables) => {
      notifySuccess(variables);
      handleSuccess?.();
    },
    onError: (error: AxiosError<{ detail?: string }>, variables) => {
      notifyError(error, variables.errorTitle);
    },
  });

  const unclaimTaskMutation = useMutation({
    mutationFn: ({ nominationId }: ClaimTaskVariables) => unclaimTaskByNominationId(nominationId),
    onSuccess: (_, variables) => {
      notifySuccess(variables);
      handleSuccess?.();
    },
    onError: (error: AxiosError<{ detail?: string }>, variables) => {
      notifyError(error, variables.errorTitle);
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: ({ nominationId, params }: CompleteTaskVariables) =>
      completeTaskByNominationId({ nominationId, params }),
    onSuccess: (_, variables) => {
      notifySuccess(variables);
      handleSuccess?.();
    },
    onError: (error: AxiosError<{ detail?: string }>, variables) => {
      notifyError(error, variables.errorTitle);
      handleError?.();
    },
  });

  return {
    claimTask: claimTaskMutation.mutate,
    unclaimTask: unclaimTaskMutation.mutate,
    completeTask: completeTaskMutation.mutate,
    isClaimTaskPending: claimTaskMutation.isPending,
    isUnclaimTaskPending: unclaimTaskMutation.isPending,
    isCompleteTaskPending: completeTaskMutation.isPending,
  };
};
